import axios from 'axios';

import * as api from './apiClient';
import * as eventQueue from './eventQueue';
import { getTarget } from './firebaseAdapter';
import * as handlers from './handlers';
import { getInstallationId } from './installationIdentity';
import { deliversWithFcm, PROVIDER_MODES, registersWeb } from './modes';
import * as permissions from './permissionSync';
import * as serviceWorker from './serviceWorker';
import * as sessions from './sessionTracker';
import * as storage from './storage';

/**
 * Notifications V2 bootstrap: a singleton that keeps this browser registered with the engine as a
 * `web` endpoint according to the brand's provider mode. Delivery starts separately
 * (`startDelivery`), and only when the brand is on `fcm_delivery` — FCM is the only push provider
 * the site carries.
 *
 * It syncs the endpoint when something may have changed: page load with a stored session, login,
 * after the permission was asked or changed in the browser, the tab coming back (debounced) and
 * once a day. A sync that would send the same data as the last one is skipped. A browser has a
 * target only after the user granted the permission; one that loses it is reported through the
 * permission endpoint. Nothing here logs the target.
 */
const log = (...args) => console.log('[NotificationsV2]', ...args);

const CONFIG_TTL_MS = 10 * 60 * 1000;
const FOREGROUND_DEBOUNCE_MS = 60 * 1000;
const PERIODIC_MS = 24 * 60 * 60 * 1000;
const LAST_SYNC_KEY = 'LAST_SYNC';
/** The installation id this browser registered with the engine, if it ever did. */
const ENDPOINT_KEY = 'ENDPOINT';

const state = {
  started: false,
  config: null,
  configFetchedAt: 0,
  configRequest: null,
  lastSyncedAt: 0,
  lastSyncFingerprint: null,
  installationId: null,
  permissionUnsubscribe: null,
  lastForegroundSyncAt: 0,
  syncing: null,
  lastError: null,
  delivery: null,
  deliveryRequest: null,
};

const hasSession = () => !!axios.defaults.headers.common.Authorization;

/**
 * Delivery side: worker listeners, sessions and the event queue, only when the brand delivers
 * with FCM. `handlePushNotification` is the site's router; `onReceived` shows a foreground push and
 * `onRead` reacts to a click that marked one read.
 */
export const startDelivery = async ({
  handlePushNotification,
  onReceived,
  onRead,
} = {}) => {
  // The handlers are kept because a page opened without a session cannot read the config: it is
  // part of the authenticated surface. `onLogin` retries with these, so delivery is not off until
  // the next page load.
  if (handlePushNotification || onReceived || onRead) {
    state.deliveryRequest = { handlePushNotification, onReceived, onRead };
  }

  const request = state.deliveryRequest || {};
  const config = await getBrandConfig();

  if (!config?.enabled || !deliversWithFcm(config.provider_mode)) {
    return { skipped: 'delivery_not_fcm' };
  }
  if (state.delivery) {
    return { skipped: 'already_started' };
  }

  state.delivery = request;
  sessions.configure({
    session_timeout_minutes: config.session_timeout_minutes,
  });
  // Handlers first: a click that opened this page makes its first session the direct one.
  await handlers.start({ config, ...request });
  sessions.start();
  eventQueue.flush().catch(() => null);

  return { started: true };
};

const fingerprintOf = body =>
  JSON.stringify([
    body.platform,
    body.target?.kind,
    body.target?.value,
    body.permission_status,
    body.notifications_enabled,
    body.app_version,
    body.locale,
    body.timezone,
    body.browser,
    body.web_origin,
    hasSession(),
  ]);

/** Brand configuration, cached for CONFIG_TTL_MS. `null` when it cannot be read. */
export const getBrandConfig = async ({ fresh = false } = {}) => {
  if (
    !fresh &&
    state.config &&
    Date.now() - state.configFetchedAt < CONFIG_TTL_MS
  ) {
    return state.config;
  }

  // `/config` is part of the client surface behind the user JWT: without a session the engine
  // answers 401, so the only useful thing to do is wait for the login that follows.
  if (!hasSession()) {
    state.lastError = 'config:no_session';
    return state.config;
  }

  if (!state.configRequest) {
    state.configRequest = (async () => {
      try {
        state.config = await api.getConfig();
        state.configFetchedAt = Date.now();
        state.lastError = null;
      } catch (err) {
        state.lastError = `config:${api.refusalOf(err) || err?.message}`;
        log('config unavailable', state.lastError);
      } finally {
        state.configRequest = null;
      }
      return state.config;
    })();
  }

  return state.configRequest;
};

export const getProviderMode = () =>
  state.config?.provider_mode || PROVIDER_MODES.ONESIGNAL_ONLY;

/** "Chrome 129", "Safari 17.4"… for the operator's push status; null when it can't be told. */
export const browserOf = (ua = navigator.userAgent || '') => {
  const rules = [
    ['Edge', /Edg(?:e|A|iOS)?\/([\d.]+)/],
    ['Opera', /(?:OPR|Opera)\/([\d.]+)/],
    ['Samsung Internet', /SamsungBrowser\/([\d.]+)/],
    ['Firefox', /(?:Firefox|FxiOS)\/([\d.]+)/],
    ['Chrome', /(?:Chrome|CriOS)\/([\d.]+)/],
    ['Safari', /Version\/([\d.]+).*Safari\//],
  ];
  const hit = rules.find(([, pattern]) => pattern.test(ua));
  if (!hit) {
    return null;
  }
  const [name, pattern] = hit;
  const version = ua.match(pattern)[1].split('.').slice(0, 2).join('.');
  return `${name} ${version}`;
};

const deviceFields = () => {
  let timezone = null;
  try {
    timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || null;
  } catch {
    timezone = null;
  }

  return {
    app_version: process.env.REACT_APP_VERSION || null,
    locale: navigator.language || null,
    timezone,
    browser: browserOf(),
    web_origin: window.location.origin,
  };
};

/**
 * A sync that never reaches the network still says why: an endpoint that is missing because the
 * permission was never granted looks exactly like one that was never attempted, and neither the
 * browser console nor the engine log would show anything at all.
 */
const skipped = (why, reason) => {
  log('endpoint sync skipped', { reason, why });
  return { skipped: why };
};

/**
 * A browser that registered before and no longer has the permission has no target to send: the
 * engine learns it through the permission endpoint instead, once per state.
 */
const reportPermissionLoss = async (installationId, permission, reason, force) => {
  if (storage.read(ENDPOINT_KEY) !== installationId) {
    return skipped('no_permission', reason);
  }

  const body = {
    permission_status: permission,
    notifications_enabled: permission !== 'denied',
  };
  const fingerprint = JSON.stringify(['permission', body, hasSession()]);

  if (!force && fingerprint === state.lastSyncFingerprint) {
    return skipped('unchanged', reason);
  }

  try {
    const data = await api.updatePermission(installationId, body);
    state.lastSyncFingerprint = fingerprint;
    state.lastError = null;
    log('permission reported', { reason, permission });
    return { synced: true, data };
  } catch (err) {
    if (api.refusalOf(err) === 'endpoint_not_found') {
      storage.remove(ENDPOINT_KEY);
      return skipped('no_permission', reason);
    }
    throw err;
  }
};

/**
 * Registers or updates this installation. Idempotent; skipped when the brand does not register
 * web endpoints or when nothing changed since the last sync.
 */
export const syncEndpoint = async ({
  reason = 'manual',
  force = false,
} = {}) => {
  // A forced sync carries news (a login, a permission answer) the one in flight may have missed.
  if (state.syncing && force) {
    await state.syncing;
  }
  if (state.syncing) {
    return state.syncing;
  }

  state.syncing = (async () => {
    try {
      const config = await getBrandConfig();

      if (!registersWeb(config)) {
        return skipped('registration_disabled', reason);
      }

      if (!hasSession()) {
        return skipped('no_session', reason);
      }

      if (!permissions.isPushSupported()) {
        return skipped('unsupported', reason);
      }

      const [installationId, permission] = await Promise.all([
        getInstallationId(),
        permissions.readPermissionStatus(),
      ]);

      state.installationId = installationId;

      if (permission !== 'authorized') {
        return await reportPermissionLoss(
          installationId,
          permission,
          reason,
          force,
        );
      }

      const target = await getTarget(config);

      if (!target?.value) {
        return skipped('no_target', reason);
      }

      const body = {
        installation_id: installationId,
        platform: 'web',
        target,
        permission_status: permission,
        notifications_enabled: permission !== 'denied',
        ...deviceFields(),
      };
      const fingerprint = fingerprintOf(body);

      if (!force && fingerprint === state.lastSyncFingerprint) {
        return skipped('unchanged', reason);
      }

      const data = await api.upsertEndpoint(body);

      state.lastSyncFingerprint = fingerprint;
      state.lastSyncedAt = Date.now();
      state.lastError = null;
      storage.write(LAST_SYNC_KEY, String(state.lastSyncedAt));
      storage.write(ENDPOINT_KEY, installationId);
      log('endpoint synced', {
        reason,
        installation_id: installationId,
        reachable: data?.reachable,
        user_bound: data?.user_bound,
      });

      return { synced: true, data };
    } catch (err) {
      state.lastError = `sync:${api.refusalOf(err) || err?.message}`;
      log('endpoint sync failed', reason, state.lastError);
      return { error: state.lastError };
    } finally {
      state.syncing = null;
    }
  })();

  return state.syncing;
};

/**
 * After the browser was asked, or when the permission changed in the browser's site settings.
 * Granted means a target to register (or rotate); anything else is reported as a permission
 * change of an endpoint registered before.
 */
export const syncPermission = async () =>
  syncEndpoint({ reason: 'permission', force: true });

/**
 * Asks the browser from a user action (the button of a prompt), then reports the answer. Never on
 * page load. `Notification.requestPermission` runs before anything is awaited, so the click counts.
 */
export const requestPermission = async () => {
  const permission = await permissions.requestPermission();
  await syncPermission();
  return permission;
};

/**
 * Whether this site can offer push to the user right now: the browser supports it and the brand
 * registers web endpoints. `permission` is the browser's answer so far.
 */
export const getPushAvailability = async () => {
  const config = await getBrandConfig();
  return {
    available: permissions.isPushSupported() && registersWeb(config),
    permission: await permissions.readPermissionStatus(),
  };
};

const handleVisibility = () => {
  if (document.visibilityState !== 'visible') {
    return;
  }
  const now = Date.now();
  if (now - state.lastForegroundSyncAt < FOREGROUND_DEBOUNCE_MS) {
    return;
  }
  state.lastForegroundSyncAt = now;

  const periodicDue = now - state.lastSyncedAt > PERIODIC_MS;
  syncEndpoint({
    reason: periodicDue ? 'periodic' : 'foreground',
    force: periodicDue,
  });
};

/** Login: the JWT is in place, so a sync binds the installation to the user and queued events can go. */
export const onLogin = async () => {
  const result = await syncEndpoint({ reason: 'login', force: true });

  if (deliversWithFcm(getProviderMode())) {
    // A page opened without a session leaves delivery off; this login is the first chance to
    // start it, and `startDelivery` opens the session of this visit itself.
    const late =
      !state.delivery && state.deliveryRequest ? await startDelivery() : null;

    eventQueue.flush({ force: true }).catch(() => null);

    if (state.delivery && !late?.started) {
      sessions.startSession();
    }
  }

  return result;
};

/**
 * Logout: unbind the installation before the session goes away. The Authorization header of this
 * instant is captured because the logout removes it right after.
 */
export const onLogout = async () => {
  const authorization = axios.defaults.headers.common.Authorization;

  state.lastSyncFingerprint = null;
  sessions.forget();
  // Events of the previous user are never attributed to the next one.
  await eventQueue.clear().catch(() => null);

  const installationId = await getInstallationId();

  if (
    !registersWeb(state.config) ||
    !authorization ||
    storage.read(ENDPOINT_KEY) !== installationId
  ) {
    return { skipped: 'registration_disabled' };
  }

  try {
    await api.unbindEndpoint(installationId, { Authorization: authorization });
    log('endpoint unbound');
    return { unbound: true };
  } catch (err) {
    log('unbind failed', api.refusalOf(err) || err?.message);
    return { error: api.refusalOf(err) || err?.message };
  }
};

/**
 * Idempotent: listeners are registered once per page. With a session already restored from
 * storage, the page load is the sync a login would have done.
 */
export const start = async () => {
  if (state.started) {
    return;
  }
  state.started = true;

  state.lastSyncedAt = Number(storage.read(LAST_SYNC_KEY)) || 0;

  document.addEventListener('visibilitychange', handleVisibility);
  state.permissionUnsubscribe = permissions.onPermissionChange(() => {
    syncPermission();
  });

  serviceWorker.unregisterLegacyWorkers();

  await getBrandConfig();

  if (hasSession()) {
    syncEndpoint({ reason: 'start' });
  }

  log('started', {
    mode: getProviderMode(),
    delivers_with_fcm: deliversWithFcm(getProviderMode()),
    registers_web: registersWeb(state.config),
  });
};

/** Tests only. */
export const stop = () => {
  document.removeEventListener('visibilitychange', handleVisibility);
  state.permissionUnsubscribe?.();
  handlers.stop();
  sessions.stop();
  state.delivery = null;
  Object.assign(state, {
    started: false,
    config: null,
    configFetchedAt: 0,
    configRequest: null,
    lastSyncedAt: 0,
    lastSyncFingerprint: null,
    installationId: null,
    permissionUnsubscribe: null,
    lastForegroundSyncAt: 0,
    syncing: null,
    lastError: null,
    deliveryRequest: null,
  });
};

/** For debugging from the console: never the target. */
export const getState = () => ({
  started: state.started,
  mode: getProviderMode(),
  enabled: !!state.config?.enabled,
  registers_web: registersWeb(state.config),
  installation_id: state.installationId,
  last_synced_at: state.lastSyncedAt ? new Date(state.lastSyncedAt) : null,
  last_error: state.lastError,
});
